import React, { useEffect } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import BookingForm from "../components/BookingFrom";
import Footer from "../components/Footer";
import PageSEO from "../components/PageSEO";
import {
	destinations,
	getDestinationBySlug,
} from "../data/destinations";

const DestinationPage = () => {
	const { slug } = useParams();
	const destination = getDestinationBySlug(slug);

	const relatedDestinations = destinations.filter((d) => d.slug !== slug);

	useEffect(() => {
		if (!destination) return;

		const script = document.createElement("script");
		script.type = "application/ld+json";
		script.id = "destination-jsonld";
		script.text = JSON.stringify({
			"@context": "https://schema.org",
			"@type": "Service",
			name: destination.modalTitle,
			description: destination.metaDescription,
			provider: {
				"@type": "LocalBusiness",
				name: "Airport Taxi Transfers Varna",
				areaServed: "Varna, Bulgaria",
			},
			offers: {
				"@type": "Offer",
				price: destination.price.replace("€", ""),
				priceCurrency: "EUR",
				description: destination.priceNote,
			},
		});
		document.head.appendChild(script);

		return () => {
			document.getElementById("destination-jsonld")?.remove();
		};
	}, [destination]);

	if (!destination) {
		return <Navigate to="/" replace />;
	}

	const scrollToBooking = () => {
		document
			.querySelector(".booking-section")
			?.scrollIntoView({ behavior: "smooth", block: "start" });
	};

	return (
		<>
			<PageSEO
				title={destination.metaTitle}
				description={destination.metaDescription}
				keywords={destination.keywords}
			/>

			<article className="destination-route-page">
				<section className="destination-hero">
					<div className="destination-hero-inner">
						<nav className="destination-breadcrumb" aria-label="Breadcrumb">
							<Link to="/">Home</Link>
							<span aria-hidden="true">/</span>
							<span>Transfers</span>
							<span aria-hidden="true">/</span>
							<span>{destination.title}</span>
						</nav>

						<span className="destination-badge">Private transfer</span>
						<h1 className="destination-hero-title">
							{destination.modalTitle}
						</h1>
						<p className="destination-hero-subtitle">
							{destination.heroSubtitle}
						</p>

						<div className="destination-hero-meta">
							<div className="destination-hero-stat">
								<span className="destination-hero-stat-label">Distance</span>
								<span className="destination-hero-stat-value">
									{destination.routeDistance}
								</span>
							</div>
							<div className="destination-hero-stat">
								<span className="destination-hero-stat-label">Duration</span>
								<span className="destination-hero-stat-value">
									{destination.routeDuration}
								</span>
							</div>
							<div className="destination-hero-stat destination-hero-stat-price">
								<span className="destination-hero-stat-label">From</span>
								<span className="destination-hero-stat-value">
									{destination.price}
									<small> {destination.priceNote}</small>
								</span>
							</div>
						</div>

						<button
							type="button"
							className="submit-button destination-hero-cta"
							onClick={scrollToBooking}
						>
							Book this transfer
						</button>
					</div>
				</section>

				<div className="destination-content-wrap">
					<div className="destination-content-grid">
						<div className="destination-main">
							<section className="destination-card">
								<h2>About this transfer</h2>
								<p className="destination-lead">{destination.description}</p>
								{destination.about.map((paragraph) => (
									<p key={paragraph.slice(0, 40)}>{paragraph}</p>
								))}
							</section>

							<section className="destination-card">
								<h2>Why choose this route</h2>
								<ul className="destination-highlights">
									{destination.highlights.map((item) => (
										<li key={item}>{item}</li>
									))}
								</ul>
							</section>

							<section className="destination-card destination-card-included">
								<h2>What&apos;s included</h2>
								<ul className="destination-included-list">
									{destination.included.map((item) => (
										<li key={item}>
											<span className="destination-included-check">✓</span>
											{item}
										</li>
									))}
								</ul>
							</section>
						</div>

						<aside className="destination-sidebar">
							<div className="destination-price-card">
								<p className="destination-price-card-label">Fixed price</p>
								<p className="destination-price-card-amount">
									{destination.price}
								</p>
								<p className="destination-price-card-note">
									{destination.priceNote} · taxes included
								</p>
								<ul className="destination-price-card-perks">
									{destination.included.slice(0, 4).map((item) => (
										<li key={item}>{item}</li>
									))}
								</ul>
								<button
									type="button"
									className="submit-button"
									onClick={scrollToBooking}
								>
									Book now
								</button>
								<a href="tel:+359888850884" className="destination-phone-link">
									Or call +359 888 850 884
								</a>
							</div>
						</aside>
					</div>

					<section className="destination-related">
						<h2>Other popular transfers</h2>
						<div className="destination-related-links">
							{relatedDestinations.map((related) => (
								<Link
									key={related.slug}
									to={`/destinations/${related.slug}`}
									className="destination-related-chip"
								>
									<span>{related.title}</span>
									<strong>{related.price}</strong>
								</Link>
							))}
						</div>
					</section>
				</div>
			</article>

			<BookingForm defaultDestination={destination.title} />
			<Footer />
		</>
	);
};

export default DestinationPage;
